import React, { useState } from 'react';
import { Calendar, ArrowLeft } from 'lucide-react';
import { AppView } from '../../types';

interface DateAgeCalculatorProps {
  onNavigateToView: (view: AppView) => void;
  defaultTab?: 'age' | 'diff';
}

export const DateAgeCalculator: React.FC<DateAgeCalculatorProps> = ({ onNavigateToView, defaultTab = 'age' }) => {
  const [subTab, setSubTab] = useState<'age' | 'diff'>(defaultTab);

  // Age State
  const [birthDate, setBirthDate] = useState<string>('1990-06-14');
  
  // Date Diff State
  const [startDate, setStartDate] = useState<string>('2026-01-01');
  const [endDate, setEndDate] = useState<string>('2026-12-31');

  // Calculate Age
  const birth = new Date(birthDate);
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  let days = now.getDate() - birth.getDate();

  if (days < 0) {
    months--;
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years--;
    months += 12;
  }

  const totalDays = Math.floor(Math.abs(now.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24));

  // Calculate Date Diff
  const startD = new Date(startDate);
  const endD = new Date(endDate);
  const diffDays = Math.floor((endD.getTime() - startD.getTime()) / (1000 * 60 * 60 * 24));
  const diffWeeks = (diffDays / 7).toFixed(1);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-16">
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('everyday-command-center')}
              className="p-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-[#0F172A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
                YamooPay Date & Calendar Engine (Prompt 14)
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">
                Calculateur d'Âge & Différence de Dates
              </h1>
            </div>
          </div>
          <button
            onClick={() => onNavigateToView('everyday-command-center')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-colors shadow-xs cursor-pointer"
          >
            Centre de Commande
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-8">
        <div className="flex border-b border-[#E2E8F0] gap-6 mb-8">
          {[
            { id: 'age', label: 'Calculateur d\'Âge Précis' },
            { id: 'diff', label: 'Différence de Dates' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id as any)}
              className={`pb-3 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                subTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {subTab === 'age' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Date de Naissance</h3>
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1">Sélectionner la date</label>
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div className="mt-4 p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#475569]">
                Calcul arithmétique calendaire rigoureux tenant compte des années bissextiles et du nombre exact de jours par mois (zéro approximation simpliste par 365).
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-teal-300 uppercase tracking-wider mb-2">Âge Exact</div>
                <div className="text-3xl font-extrabold tracking-tight mt-1">
                  {years} ans, {months} mois, {days} jours
                </div>
                <div className="mt-4 text-xs text-teal-200">
                  Total de jours vécus : <strong className="text-white">{totalDays.toLocaleString()} jours</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {subTab === 'diff' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Intervalle de Dates</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Date de début</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Date de fin</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-teal-300 uppercase tracking-wider mb-2">Différence</div>
                <div className="text-3xl font-extrabold tracking-tight mt-1">
                  {diffDays.toLocaleString()} jours
                </div>
                <div className="mt-4 text-xs text-teal-200">
                  Soit environ <strong className="text-white">{diffWeeks} semaines</strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
